import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { QZHIDDeviceReadResult, HIDReadStatus, WeightUnits, QZHIDDevice, QZHIDScaleFormattedPayload } from './types'
import qz from './qz'
import PrinterToaster from './toaster'
import { AppState } from '../store'
import { setCurrentHIDDevice } from '../current/actions'

// If you are looking at this file because you want to use a scale in a component, skip straight to useScaleStreamer

const GRAM_TO_POUND = 0.00220462262185
const KILOGRAM_TO_POUND = GRAM_TO_POUND * 1000
const OZ_TO_POUND = 0.0625

export function getHIDReadStatus(i: number): HIDReadStatus {
  switch (i) {
    case 1: // fault
    case 5: // underweight
    case 6: // overweight
    case 7: // calibrate
    case 8: // re-zero
      return 'error'
    case 3: // busy
      return 'busy'
    case 2: // stable at zero
    case 4: // stable non-zero
    default:
      return 'stable'
  }
}

export function getHIDReadUnits(i: number): WeightUnits {
  switch (i) {
    case 2:
      return 'g'
    case 3:
      return 'kg'
    case 11:
      return 'oz'
    case 12:
    default:
      return 'lbs'
  }
}

const round = (w: number) => Math.round(100 * w) / 100

export function convertToLBS(units: WeightUnits, weight: number): number {
  if (units === 'lbs') return round(weight)
  if (units === 'g') return round(weight * GRAM_TO_POUND)
  if (units === 'kg') return round(weight * KILOGRAM_TO_POUND)
  if (units === 'oz') return round(weight * OZ_TO_POUND)
  return null
}

export function parseHIDScaleOutput(data: any[]): QZHIDDeviceReadResult {
  // Filter erroneous data
  if (data.length < 4 || data.slice(2, 8).join('') === '000000000000') {
    return null
  }

  // Get status
  const status = getHIDReadStatus(parseInt(data[1], 16))

  // Get precision
  let precision = parseInt(data[3], 16)

  // eslint-disable-next-line no-bitwise
  precision ^= -256 // unsigned to signed

  // xor on 0 causes issues
  if (precision === -256) {
    precision = 0
  }

  // Get units
  const units = getHIDReadUnits(parseInt(data[2], 16))

  // Get weight
  data.splice(0, 4)
  data.reverse()
  let weight: any = parseInt(data.join(''), 16)

  weight *= 10 ** precision
  weight = weight.toFixed(Math.abs(precision))

  return {
    raw: Math.round(weight * 100) / 100,
    weightInPounds: Math.round(convertToLBS(units, weight) * 100) / 100,
    units,
    status
  }
}

export function parsePayload(payload: any) {
  const response: QZHIDScaleFormattedPayload = {
    output: payload.output ? parseHIDScaleOutput(payload.output) : null,
    exception: payload.exception,
    action: payload.actionType,
    vendorId: payload.vendorId,
    productId: payload.productId
  }

  return response
}

export function displayScaleError(message: string): void {
  PrinterToaster.show({ intent: 'danger', message })
}

// Closes the stream to a device
// note: the stopListening param will stop listening across all HID devices
async function closeDevice(device: QZHIDDevice): Promise<boolean> {
  try {
    const claimed = await qz.hid.isClaimed(device.vendorId, device.productId)

    // If the device isn't claimed, nothing needs to be done
    if (!claimed) return true

    try {
      await qz.hid.closeStream(device.vendorId, device.productId)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
    try {
      await qz.hid.releaseDevice(device.vendorId, device.productId)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }

    return true
  } catch (e) {
    return false
  }
}

interface OpenDeviceArgs {
  device: QZHIDDevice
  onDataExceptionReceived?: (exception: string) => void
  onDataOutputReceived?: (output: QZHIDDeviceReadResult) => void
}

interface OpenDeviceReturn {
  isClaimed?: boolean
  streamStarted?: boolean
}

async function openDevice(args: OpenDeviceArgs): Promise<OpenDeviceReturn> {
  const { device, onDataExceptionReceived, onDataOutputReceived } = args
  const payload: OpenDeviceReturn = {}

  if (!device) {
    payload.isClaimed = false
    payload.streamStarted = false
    return payload
  }
  await qz.hid.setHidCallbacks([
    o => {
      const response: QZHIDScaleFormattedPayload = parsePayload(o)

      // Ignore data from other streams
      if (response.vendorId !== device.vendorId || response.productId !== device.productId) return

      if (response.exception) {
        if (onDataExceptionReceived) onDataExceptionReceived(response.exception)
        return
      }
      if (response.output && onDataOutputReceived) onDataOutputReceived(response.output)
    }
  ])

  payload.isClaimed = await qz.hid.isClaimed(device.vendorId, device.productId)

  // Only attempt to claim if not claimed
  if (!payload.isClaimed) {
    await qz.hid.claimDevice(device.vendorId, device.productId)
    payload.isClaimed = await qz.hid.isClaimed(device.vendorId, device.productId)
  }

  if (!payload.isClaimed) {
    payload.streamStarted = false
    return payload
  }

  try {
    await qz.hid.openStream(device.vendorId, device.productId, '8', 16)
    payload.streamStarted = true
  } catch (e) {
    payload.streamStarted = false
    // eslint-disable-next-line no-console
    console.log(e)
  }

  return payload
}

export const useScaleStreamer = () => {
  const dispatch = useDispatch()
  const storedScale = useSelector((s: AppState) => s.current.currentHIDDevice)
  const qzConnected = useSelector((s: AppState) => s.current.qzConnection.connected)
  const [isStreaming, setIsStreaming] = React.useState(false)
  const [initialized, setInitialized] = React.useState(false)
  const [currentScaleValue, setCurrentScaleValue] = React.useState(null)

  const scaleExists = scale => {
    return scale && scale.vendorId && scale.productId
  }

  // Allow startScale to view the latest value of currentScaleValue
  // https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
  const scaleValueRef = React.useRef(null)
  scaleValueRef.current = currentScaleValue

  const startScale = async scale => {
    const res = await openDevice({
      device: scale,
      onDataExceptionReceived: e => {
        displayScaleError(`Scale Error: ${e}`)
        setIsStreaming(false)
        dispatch(setCurrentHIDDevice(null))
      },
      onDataOutputReceived: data => {
        // onDataOutputReceived fires constantly, even when the values aren't changing.
        // Avoid setting setCurrentScaleValue constantly, so we avoid constantly re-rendering the consuming component
        // which can prevent a Button onClick function from firing ("Add another package" button).
        if (data.weightInPounds !== scaleValueRef.current?.weightInPounds) {
          setCurrentScaleValue(data)
        }
      }
    })
    if (res.isClaimed) {
      setIsStreaming(true)
    } else {
      setIsStreaming(false)
    }
  }

  React.useEffect(() => {
    if (!qzConnected) {
      setInitialized(false)
      setIsStreaming(false)
      setCurrentScaleValue(null)
    } else {
      if (!initialized) {
        setInitialized(true)
        qz.hid.startListening()
      }
      if (scaleExists(storedScale)) {
        startScale(storedScale)
      } else {
        setIsStreaming(false)
      }
    }

    return () => {
      if (storedScale) {
        closeDevice(storedScale)
      }
    }
    // Scale integration is touchy, and it's working right now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedScale, qzConnected])

  return [
    storedScale ? storedScale.product : null,
    currentScaleValue ? currentScaleValue.weightInPounds : null,
    isStreaming
  ]
}
